import React, { useEffect, useState } from 'react';
import classNames from 'classnames';
import { Container, Content, DOMHelper, useToaster } from 'rsuite';
import Header from './Header';
import { isExpanded } from '@monorepo/shared/store/sidemenuSlice';
import serviceTermPdf from '/SD-FortiSOC-Cloud.pdf';
import { Footer } from '@monorepo/shared/components';
import { EnvConst, HomeElms, host, server } from '../config/const';
import {
  getSafeUrl,
  ModalAlert,
  useAppSelector,
  useLazyGetUserPermissionQuery,
  useLazyRegionLoginQuery,
  userPermissionCheck
} from '@monorepo/shared';
import {
  selectRegions,
  selectSessionStatus
} from '@monorepo/shared/store/authSlice';
import './Frame.scss';

import '@monorepo/shared/i18n/config';
import { Sidemenu } from './SidemenuNew';
import {
  selectAccountWithPermission,
  selectCurrentAccount
} from '@monorepo/shared/store/accountSlice';
import { useNavigate } from 'react-router-dom';
import { t } from 'i18next';
import { ErrorToaster } from '../pages/Account/Warning';
import { selectHasGroup } from '@monorepo/shared/store/groupSlice';

const { getHeight, on } = DOMHelper;

export interface FrameProps {
  hideSideMenu?: boolean;
  hideHeader?: boolean;
  hideFooter?: boolean;
  children?: React.ReactNode;
}

const Frame = ({
  children,
  hideSideMenu,
  hideHeader,
  hideFooter
}: FrameProps) => {
  const navigate = useNavigate();
  const toaster = useToaster();
  const [regionLogin, { error, isError }] = useLazyRegionLoginQuery();
  const [getUserPermission, { error: permError, isError: isPermError }] =
    useLazyGetUserPermissionQuery();
  const menuExpanded = useAppSelector(isExpanded);
  const hasGroup = useAppSelector(selectHasGroup);
  const sessionStatus = useAppSelector(selectSessionStatus);
  const currentAccount = useAppSelector(selectCurrentAccount);
  const account = useAppSelector(selectAccountWithPermission);

  const regions = useAppSelector(selectRegions);
  const [windowHeight, setWindowHeight] = useState<number>(getHeight(window));
  const headerHeight = 52;
  const footerHeight = 40;
  const [containerHeight, setContainerHeight] = useState<number>(windowHeight);
  const [alertOpen, setAlertOpen] = useState<boolean>(false);
  const logOutURL = getSafeUrl(`${server}`);
  const logInUrl = getSafeUrl(
    `${EnvConst.auth_host}/ssologin/?next=${host}/cred/ou`
  );

  useEffect(() => {
    setWindowHeight(getHeight(window));
    const resizeListener = on(window, 'resize', () =>
      setWindowHeight(getHeight(window))
    );

    return () => {
      resizeListener.off();
    };
  }, []);

  useEffect(() => {
    if (sessionStatus === 'EXPIRED') {
      setAlertOpen(true);
    } else if (sessionStatus === 'REVOKED') {
      window.open(getSafeUrl(`${server}`), '_self');
    }
  }, [sessionStatus]);

  const containerClasses = classNames('page-container', {
    'container-full': !menuExpanded
  });

  useEffect(() => {
    let height = windowHeight;
    if (!hideHeader) {
      height -= headerHeight;
    }
    if (!hideFooter) {
      height -= footerHeight;
    }

    setContainerHeight(height);
  }, [hideHeader, hideFooter, windowHeight]);

  const getPermission = (account: Record<string, any>) => {
    if (account.access_from && !account.portals) {
      getUserPermission({
        account_node_id: account?.account_node_id,
        account_id: account?.account_id,
        user_id: account?.user_id,
        access_from_user_id: account?.access_from?.user_id,
        access_from_account_id: account?.access_from?.account_id,
        server: server
      });
    }
  };
  useEffect(() => {
    if (currentAccount?.account_id) {
      const region = currentAccount?.region || {};
      const storedToken = region?.alias ? regions?.[region.alias]?.token : '';
      if (
        region?.url &&
        region?.globalToken &&
        !region?.token &&
        !storedToken
      ) {
        regionLogin({ region: currentAccount?.region, enableTimezone: true });
      }
      console.info('------ current account changed', currentAccount);
      getPermission(currentAccount);
    }
  }, [currentAccount]);

  useEffect(() => {
    if (account.portals) {
      const is_check_pass =
        account?.group_id || userPermissionCheck(account, HomeElms?.fcName);
      if (!is_check_pass) {
        console.warn('------ user permission check failed', account);
        if (account?.user_auth_url && window) {
          window.open(getSafeUrl(`${account.user_auth_url}`), '_self');
        } else {
          navigate('/cred/no-permission');
        }
      } else if (account?.region?.url) {
        const regionUrl = account?.region?.url;
        const regionAlias = account?.region?.alias;
        if (regionAlias && regionUrl && regions[regionAlias]) {
          const regionData = regions[regionAlias];
          regionData.globalToken &&
            !regionData.token &&
            regionLogin({ region: regionData, enableTimezone: true });
        }
      } else if (account?.region?.length) {
        const accountRegions = account?.region;
        accountRegions.forEach((region: Record<string, any>) => {
          const regionAlias = region?.alias;
          if (regionAlias && regions[regionAlias]) {
            const regionData = regions[regionAlias];
            regionData.globalToken &&
              !regionData.token &&
              regionLogin({ region: regionData, enableTimezone: true });
          }
        });
      }
    }
  }, [account]);

  useEffect(() => {
    if (isError) {
      console.error('Error occurred:', error);
      // const regionErrMessage = (
      //   <Message
      //     showIcon
      //     type="warning"
      //     closable
      //     header={t('common.apiMsg.warning.region')}
      //   >
      //     <span>
      //       <b>{currentAccount?.region?.alias}</b>
      //     </span>
      //   </Message>
      // );
      // toaster.push(regionErrMessage, {
      //   placement: 'bottomEnd',
      //   duration: 5000
      // });
      ErrorToaster({
        toaster,
        error,
        title: `${t('common.apiMsg.warning.region')}`
      });
    }
  }, [isError, error]);
  useEffect(() => {
    if (isPermError) {
      console.error('Error occurred:', permError);
      ErrorToaster({
        toaster,
        error: permError,
        title: `get user permission failed`
      });
    }
  }, [isPermError, permError]);

  return (
    <div style={{ height: windowHeight }}>
      <Header
        hideSideMenu={hideSideMenu}
        style={{
          height: hideHeader ? 0 : headerHeight,
          display: hideHeader ? 'none' : 'flex'
        }}
      />
      <Container
        className="frame-container"
        style={{ height: containerHeight }}
      >
        <Container
          className={containerClasses}
          style={{
            height: `${containerHeight}px`
          }}
        >
          {!hideSideMenu && hasGroup && <Sidemenu />}
          <Content
            style={{
              // height: `${containerHeight}px`
              height: '100%',
              overflow: 'hidden'
            }}
          >
            {children}
          </Content>
        </Container>
      </Container>
      <Footer
        pdf={serviceTermPdf}
        style={{
          height: hideFooter ? 0 : footerHeight,
          display: hideFooter ? 'none' : 'flex',
          background: '#000',
          color: '#b0b0b0'
        }}
        server={server}
      ></Footer>
      <ModalAlert
        open={alertOpen}
        setOpen={setAlertOpen}
        logInUrl={logInUrl}
        logOutUrl={logOutURL}
      ></ModalAlert>
    </div>
  );
};

export default Frame;
